import React, { useMemo } from 'react';
import { useStreamingText } from '@/hooks/use-streaming-text';
import { cn } from '@/lib/utils';

interface StreamingTextProps {
  text: string;
  className?: string;
  /**
   * Speed of animation in milliseconds per word
   * @default 50
   */
  speed?: number;
  /**
   * Whether to animate word-by-word (true) or character-by-character (false)
   * @default true
   */
  wordByWord?: boolean;
  /**
   * Whether to render as markdown
   */
  asMarkdown?: boolean;
  /**
   * Custom markdown renderer
   */
  renderMarkdown?: (text: string) => { __html: string };
}

/**
 * Component that renders text with smooth streaming animation
 * Similar to ChatGPT's text reveal effect
 */
export function StreamingText({
  text,
  className,
  speed = 50,
  wordByWord = true,
  asMarkdown = false,
  renderMarkdown,
}: StreamingTextProps) {
  const displayedText = useStreamingText(text, {
    speed,
    wordByWord,
    respectReducedMotion: true,
    autoStart: true,
  });

  // For markdown rendering - smooth fade-in
  if (asMarkdown && renderMarkdown) {
    const html = renderMarkdown(displayedText);
    return (
      <div className={cn('relative inline-block streaming-text-container', className)}>
        <div
          className="text-foreground whitespace-pre-wrap leading-snug streaming-text-content"
          dangerouslySetInnerHTML={html}
        />
      </div>
    );
  }

  // Plain text rendering with word wrapping for smooth fade-in
  const words = useMemo(() => {
    if (!wordByWord) return null;
    return displayedText.split(/(\s+)/);
  }, [displayedText, wordByWord]);

  if (words && wordByWord) {
    return (
      <div className={cn('relative streaming-text-container', className)}>
        <span className="text-foreground whitespace-pre-wrap leading-snug streaming-text-content">
          {words.map((word, index) => (
            <span key={index} className="streaming-word">
              {word}
            </span>
          ))}
        </span>
      </div>
    );
  }

  // Fallback for character-by-character or non-word mode
  return (
    <div className={cn('relative streaming-text-container', className)}>
      <span className="text-foreground whitespace-pre-wrap leading-snug streaming-text-content">{displayedText}</span>
    </div>
  );
}

